import { createContext } from "react";
import haversine from "haversine-distance";
import {
  Provider,
  SearchProviderSelectedFilterType,
} from "./SearchProvider.types";
import { Option } from "react-google-places-autocomplete/build/types";

export type NearbyRegion = {
  name: string;
  place_id: string;
};

export type SearchProviderContextType = {
  totalMatchingProviders: number;
  providers: Provider[];
  showResults: boolean;
  setShowResults: (showResults: boolean) => void;
  setProviders: (providers: Provider[]) => void;
  setLoading: (loading: boolean) => void;
  manualPlace: Option | null;
  supportsMap: boolean;
  setNearbyRegions: (regions: NearbyRegion[]) => void;
};

export type Coordinates = {
  lat: number;
  lng: number;
  range: number; // in km
};

export const SearchProviderContext = createContext<SearchProviderContextType>({
  totalMatchingProviders: 0,
  providers: [],
  supportsMap: false,
  showResults: false,
  manualPlace: null,
  setShowResults: () => {},
  setProviders: () => {},
  setLoading: () => {},
  setNearbyRegions: () => {},
});

/**
 * Takes a list of providers, and a list of filters, and returns a list of
 * providers that match the filters.
 */
export const doProviderFilter = (
  filters: SearchProviderSelectedFilterType[],
  providers: Provider[],
  coords?: Coordinates
): Provider[] => {
  let includeOnline = true;
  let includeInPerson = true;

  if (filters) {
    filters.forEach((filter) => {
      if (filter.name === "filter__person") {
        if (filter.value === "Online") {
          includeInPerson = false;
        } else if (filter.value === "In-person") {
          includeOnline = false;
        }
      }
    });
  }

  let result = providers;

  if (coords && coords.lat && coords.lng && coords.range) {
    // filter by distance
    result = result.filter((provider) => {
      return checkProviderCovers(provider, coords, {
        includeOnline,
        includeInPerson,
      });
    });
  }

  if (!filters || filters.length === 0) {
    // no additional filters, return the original list
    return result;
  }

  filters.forEach((filter) => {
    if (filter.name === "filter__person") {
      if (!coords) {
        // filter by the fact that we only want them to do *any*
        // in -person as we've already filtered by distance
        const target = includeOnline ? "Online" : "In-person";
        result = result.filter((provider) => {
          const inPersonField = provider.fields.find(
            (field) => field.label === target
          );

          return inPersonField;
        });
      }
      return;
    }
    // by default look for a string match on the value
    result = result.filter((provider) => {
      const field = provider.fields.find((field) => field.name === filter.name);

      if (!field) {
        return false;
      }

      if (typeof field.value === "string") {
        return field.value === filter.value;
      }

      if (Array.isArray(field.value)) {
        return field.value.find((value) => value === filter.value);
      }

      return false;
    });
  });

  return result;
};

/**
 * Helper method to check whether a given Provider covers a given location.
 *
 *
 */
export const checkProviderCovers = (
  provider: Provider,
  coords: Coordinates,
  filters: { includeOnline: boolean; includeInPerson: boolean }
): boolean => {
  let isInLocation = false;
  let distance = 0;

  const { includeOnline, includeInPerson } = filters;

  // range is km, haversine expects meters so multiply by 1000
  const range = coords.range * 1000;

  if (provider.latitude && provider.longitude) {
    distance = haversine(
      {
        lat: parseFloat(provider.latitude),
        lng: parseFloat(provider.longitude),
      },
      coords
    );

    const providerInRange = Math.abs(distance) <= range;

    if (providerInRange) {
      isInLocation = true;
    }
  }

  if (isInLocation) {
    return true;
  }

  if (includeInPerson) {
    // also check the fields 'In-person' and 'Online' to see if any
    // of those are selected within the range
    const inPersonField = provider.fields.find(
      (field) => field.label === "In-person"
    );

    if (inPersonField && Array.isArray(inPersonField.value)) {
      inPersonField.value?.forEach((value) => {
        if (!value.latitude || !value.longitude) {
          return;
        }

        const distance = haversine(value, coords);
        let isInCoveredLocation = distance <= range;

        if (isInCoveredLocation) {
          isInLocation = true;
        }
      });

      if (isInLocation) {
        return true;
      }
    }
  }

  if (includeOnline) {
    const onlineField = provider.fields.find(
      (field) => field.label === "Online"
    );

    if (onlineField && Array.isArray(onlineField.value)) {
      onlineField.value?.forEach((value) => {
        if (!value.latitude || !value.longitude) {
          return;
        }

        const distance = haversine(value, coords);
        let isInCoveredLocation = distance <= range;

        if (isInCoveredLocation) {
          isInLocation = true;
        }
      });

      if (isInLocation) {
        return true;
      }
    }
  }

  return false;
};
